import type { AppLocale, Messages } from "./types";
import type { LandingStrings } from "./landingTypes";
import { getIntlLocale, isRtlLocale } from "./resolveLocale";
import { TRANSLATIONS } from "./translations";
import { WEATHER_I18N } from "./weatherI18n";

export type { AppLocale, Messages, ScheduleLabels } from "./types";
export type { LandingCoreStrings, LandingFaqItem, LandingSlideCopy, LandingStrings } from "./landingTypes";
export {
  getIntlLocale,
  isRtlLocale,
  resolveAppLocale,
  ALL_APP_LOCALES,
  LOCALE_LABELS,
  LOCALE_SELECT_LABELS,
  persistAppLocale,
  parseLocaleParam,
} from "./resolveLocale";
export { TRANSLATIONS } from "./translations";
export { getLandingStrings, LANDING_TRANSLATIONS } from "./landingTranslations";

export function getMessages(locale: AppLocale): Messages {
  const base = TRANSLATIONS[locale] ?? TRANSLATIONS.en;
  const wx = WEATHER_I18N[locale];
  return wx ? { ...base, ...wx } : base;
}

/** Ana sayfa landing — `<title>` ve `lang` / `dir`. */
export function applyLandingDocumentLocale(locale: AppLocale, landing: LandingStrings): void {
  document.documentElement.lang = getIntlLocale(locale);
  document.documentElement.dir = isRtlLocale(locale) ? "rtl" : "ltr";
  document.title = `Wedy.Me · ${landing.pageMetaTitle}`;
}

/** Gizlilik / şartlar — `<title>` ve `lang` / `dir`. */
export function applyLegalDocumentLocale(
  locale: AppLocale,
  landing: LandingStrings,
  kind: "privacy" | "terms",
): void {
  document.documentElement.lang = getIntlLocale(locale);
  document.documentElement.dir = isRtlLocale(locale) ? "rtl" : "ltr";
  const label = kind === "privacy" ? landing.footerPrivacy : landing.footerTerms;
  document.title = `Wedy.Me · ${label}`;
}

/** <html lang/dir>, başlık, footer metinleri (statik HTML). */
export function applyDocumentLocale(locale: AppLocale, messages: Messages): void {
  document.documentElement.lang = getIntlLocale(locale);
  document.documentElement.dir = isRtlLocale(locale) ? "rtl" : "ltr";
  document.title = `Wedy.Me · ${messages.metaTitle}`;

  const promoText = document.querySelector(".wedy-app-promo__text");
  if (promoText) promoText.textContent = messages.appPromoText;

  const promoLink = document.querySelector(".wedy-app-promo__link");
  if (promoLink) promoLink.setAttribute("aria-label", messages.downloadAppStoreAria);

  const badge = document.querySelector(".wedy-app-promo__badge");
  if (badge) badge.setAttribute("alt", messages.downloadAppStoreAria);
}

/** Yüklenen davet: `Wedy.Me · Gelin & Damat · etkinlik adı` (etkinlik yoksa yerelleştirilmiş «Düğün/Wedding»). */
export function inviteDocumentTitle(
  wedding: { brideName: string; groomName: string; eventTitle: string },
  messages: Messages,
): string {
  const b = wedding.brideName.trim();
  const g = wedding.groomName.trim();
  let couple = "";
  if (b && g) couple = `${b} & ${g}`;
  else if (b) couple = b;
  else if (g) couple = g;

  const et = wedding.eventTitle.trim();
  const eventPart = et || messages.invitePageTitleFallbackWedding;

  const parts: string[] = ["Wedy.Me"];
  if (couple) parts.push(couple);
  parts.push(eventPart);
  return parts.join(" · ");
}

export function formatGuestCapacity(m: Messages, guestLimit: number): string {
  return guestLimit === 1 ? m.rsvpCapacityOne : m.rsvpCapacityMany.replace("{n}", String(guestLimit));
}

export function guestNumberLabel(m: Messages, indexOneBased: number): string {
  return m.guestNumber.replace("{n}", String(indexOneBased));
}
